import { DateRangePicker, type DateRangeOption } from '../date-range-picker/date-range-picker.component';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import type { DateRange } from '@daypicker/react';
import { Menu } from '../menu/menu.component';
import { Icon } from '../icon/icon.component';
import { FieldError } from '../field-error/field-error.component';
import { style } from './date-range.module.css';

interface Props {
  label?: string;
  value?: DateRange;
  options?: DateRangeOption[];
  disabled?: boolean;
  error?: string;
  onChange: (value: DateRange) => void;
}

export function DateRange(props: Props) {

  const {
    label,
    value,
    options,
    disabled,
    error,
    onChange
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const [openPopup, setOpenPopup] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(options?.[0]);
  const [month, setMonth] = useState(dateRange?.to);
  const [applied] = useState({ value: false });

  function handleChange(value: DateRange) {
    setDateRange(value);
  }

  function handleClick() {
    if (dateRange) {
      onChange(dateRange);
      setOpenPopup(false);
      applied.value = true;
    }
  }

  function handleClose() {
    setOpenPopup(false);
    if (!applied.value) setDateRange(value);
    applied.value = false;
  }

  function getDisplayValue() {
    const option = options?.find(option => isOptionSelected(option, value));
    if (option) {
      return option.title;
    } else {
      return `${formatDate(value?.from)} - ${formatDate(value?.to)}`;
    }
  }

  function isOptionSelected(option: DateRangeOption, dateRange: DateRange | undefined) {
    return (
      option.from.toLocaleDateString() === dateRange?.from?.toLocaleDateString() && 
      option.to.toLocaleDateString() === dateRange.to?.toLocaleDateString()
    );
  }
  
  function formatDate(date?: Date) {
    return date
      ? Intl.DateTimeFormat('es-DO', { dateStyle: 'medium' }).format(new Date(date))
      : 'Ninguna';
  }

  return (
    <div className={clsx(style.container, disabled && style.disabled)}>
      {label && 
        <label className={style.label}>{label}</label>
      }
      <div 
        ref={ref} 
        tabIndex={1}
        className={style.input}
        onClick={() => setOpenPopup(open => !open)}>
        <Icon className={style.icon} icon='today'/>
        <span className={style.value}>{getDisplayValue()}</span>
        <Icon className={style.icon} icon='expand_more'/>
      </div>
      <FieldError message={error}/>
      <Menu
        open={openPopup} 
        anchor={ref} 
        top={8}
        autoWith={true}
        style={{ container: style.popup }}
        onClose={handleClose}>
        <DateRangePicker
          value={dateRange}
          month={month}
          options={options}
          onChange={handleChange}
          onMonthChange={setMonth}
          onApply={handleClick}/>
      </Menu>
    </div>
  );
}
